import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import { MouseEvent, useCallback } from 'react';

import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { Dropdown } from 'UIPackage/MainModule/components/Dropdown';
import { IDropdownItem } from 'UIPackage/MainModule/interfaces/IDropdownItem';
import { ExportFormatsEnum } from 'APIPackage/ImportExportModule/enums/ExportFormatsEnum';
import { ExportSimilaritiesListTypesEnum } from 'APIPackage/SimilarityModule/enums/ExportSimilaritiesListTypesEnum';
import { DropdownItem } from 'UIPackage/MainModule/components/DropdownItem';
import {
  getUserRoleIsProsecutorOrSuperProsecutor,
  getUserRoleIsSupervisor,
  getUserRoleIsOmbudsman,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { mapKnmFilterToRaw } from 'KnmListPackage/KnmListFiltersModule/common/mapKnmFilterToRaw';
import { getKnmListFilterCurrentFilter } from 'KnmListPackage/KnmListFiltersModule/selectors/knmListFilterSelectors';
import {
  knmListExportList,
  knmListExportRegistryKnm,
  knmListExportSimilaritiesList,
  knmListRemoveFromPlanClick,
} from 'KnmListPackage/MainModule/actions/knmListActions';
import { getKnmListSearchValue } from 'KnmListPackage/MainModule/selectors/knmListSelectors';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { IExport } from 'APIPackage/ImportExportModule/interfaces/IExport';
import { IKnmListFilter } from 'KnmListPackage/KnmListFiltersModule/interfaces/IKnmListFilter';
import { planListExportDocsForKno, planListExportPlanById } from 'PlanListPackage/MainModule/actions/planListActions';
import { IKnmCheckSubjectResult } from 'APIPackage/KnmModule/interfaces/IKnmCheckSubjectResult';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';
import { IconInfo } from 'UIPackage/IconsModule/components/IconInfo';
import { KnmErknmTypeIdsByCodeEnum } from 'APIPackage/CatalogsModule/enums/KnmErknmTypeIdsByCodeEnum';
import { KnmTypeIdsByCodeEnum } from 'APIPackage/CatalogsModule/enums/KnmTypeIdsByCodeEnum';
import { IKnmRaw } from 'KnmListPackage/MainModule/interfaces/IKnmRaw';
import { ITableColumn } from 'CorePackage/TableModule/interfaces/ITableColumn';
import { KnmErknmStatusIdsByCodeEnum } from 'APIPackage/CatalogsModule/enums/KnmErknmStatusIdsByCodeEnum';
import { addNoticeImportNotification } from 'CorePackage/NotificationModule/common/addNoticeImportNotification';

import styles from './KnmListDropdown.module.scss';

interface IKnmListPanelProps {
  dataCount: number;
  columns: ITableColumn<IKnmRaw>[];
  onRestoreClick: () => void;
  knms: IKnmRaw[];
  isErknm: boolean;
  isProsecutorOrSuperProsecutor: boolean;
  isSupervisor: boolean;
  isOmbudsman: boolean;
  activeKnmFilter: IKnmListFilter | undefined;
  selectedKnmsIds: string[];
  selectedKnmsNumber: number;
  isDeleteButtonDisabled: boolean;
  disabledExport: boolean;
  handleDropdownDeleteButtonClick: () => void;
  openRemoveFromPlanModal: () => void;
  openUpdateKnmsStartDateModal: () => void;
  openUpdateKnmsDurationModal: () => void;
  openDisapproveKnmsModal: () => void;
  handleCancelKnmsButtonClick: () => void;
}

const KnmListDropdownWithoutStore = ({
  dataCount,
  columns,
  onRestoreClick,
  knms,
  isErknm,
  isProsecutorOrSuperProsecutor,
  isSupervisor,
  isOmbudsman,
  activeKnmFilter,
  selectedKnmsIds,
  selectedKnmsNumber,
  isDeleteButtonDisabled,
  disabledExport,
  handleDropdownDeleteButtonClick,
  openRemoveFromPlanModal,
  openUpdateKnmsStartDateModal,
  openUpdateKnmsDurationModal,
  openDisapproveKnmsModal,
  handleCancelKnmsButtonClick,
}: IKnmListPanelProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const currentPlanId = knms.find((knm: IKnmRaw) => String(knm.id) === selectedKnmsIds[0])?.planId;
  const isSingleKnmWithPlanSelected =
    selectedKnmsNumber === 1 && knms.some((knm: IKnmRaw) => String(knm.id) === selectedKnmsIds[0] && knm.planId);
  const isSingleKnmSelected =
    selectedKnmsNumber === 1 && knms.some((knm: IKnmRaw) => String(knm.id) === selectedKnmsIds[0]);
  const isSelectedIsSpvRegistry =
    selectedKnmsNumber === 1 &&
    knms.some((knm: IKnmRaw) => String(knm.id) === selectedKnmsIds[0] && knm.spvRegistryExists);
  const isSelectedIsTorRegistry =
    selectedKnmsNumber === 1 &&
    knms.some((knm: IKnmRaw) => String(knm.id) === selectedKnmsIds[0] && knm.torRegistryExists);
  const isAllSelectedKnmsWithPlan =
    selectedKnmsNumber > 0 && selectedKnmsIds.some(id => !!knms.find(k => String(k.id) === id)?.planId);
  const filteredKnms = knms.filter(el => selectedKnmsIds.find(knmId => Number(knmId) === el.id));

  const isSomeSelectedKnmDeleted = selectedKnmsIds.some(knmId =>
    Boolean(knms.find(knm => knmId === String(knm.id))?.deleted),
  );

  const selectedKnmTypeIds = selectedKnmsIds.reduce(
    (uniqueKnmTypeIds: Array<IKnmRaw['knmTypeId'] | undefined>, knmId) => {
      const knmTypeId = knms.find(knm => knmId === String(knm.id))?.knmTypeId;

      if (typeof knmTypeId === 'number' && !uniqueKnmTypeIds.includes(knmTypeId)) {
        uniqueKnmTypeIds.push(knmTypeId);
      }

      return uniqueKnmTypeIds;
    },
    [],
  );

  const isSelectedKnmsDisapproveAvailable =
    selectedKnmsNumber > 0 &&
    selectedKnmTypeIds.length === 1 &&
    selectedKnmsIds.every(knmId => {
      const knm = knms.find(knmItem => knmId === String(knmItem.id));

      return (
        !knm?.knmAnnulled &&
        (knm?.knmTypeId === KnmTypeIdsByCodeEnum.PP ||
          (knm?.knmTypeId === KnmTypeIdsByCodeEnum.VP && knm?.approveRequired))
      );
    });

  const isSelectedKnmsTypePlan =
    selectedKnmsNumber > 0 &&
    selectedKnmsIds.every(knmId => {
      const knmTypeId = knms.find(knm => knmId === String(knm.id))?.knmTypeId;

      return knmTypeId === KnmTypeIdsByCodeEnum.PP || knmTypeId === KnmErknmTypeIdsByCodeEnum.PP;
    });

  const isSomeSelectedKnmCreatedByErpDb = selectedKnmsIds.some(knmId =>
    Boolean(knms.find(knm => knmId === String(knm.id))?.createdByErpDb),
  );

  const isAvailableKnmsToPlanOut =
    isAllSelectedKnmsWithPlan &&
    filteredKnms.every(
      knm =>
        knm.statusId === KnmErknmStatusIdsByCodeEnum.TYPE_WAITING_CARRY_OUT ||
        knm.statusId === KnmErknmStatusIdsByCodeEnum.TYPE_CARRIED_OUT,
    );

  const isDropdownPlanOutItemsDisabled =
    !isAllSelectedKnmsWithPlan || isSomeSelectedKnmCreatedByErpDb || !isAvailableKnmsToPlanOut;

  const isDropdownRestoreItemsDisabled =
    selectedKnmsNumber === 0 ||
    (selectedKnmsNumber > 0 && !isSomeSelectedKnmDeleted) ||
    isSomeSelectedKnmCreatedByErpDb;

  const isDropdownStartDateUpdateItemDisabled =
    selectedKnmsNumber === 0 || isSomeSelectedKnmDeleted || isSomeSelectedKnmCreatedByErpDb;

  const isDropdownDurationUpdateItemDisabled =
    selectedKnmsNumber === 0 || !isSelectedKnmsTypePlan || isSomeSelectedKnmDeleted || isSomeSelectedKnmCreatedByErpDb;

  const isDropdownDisapproveItemsDisabled = !isSelectedKnmsDisapproveAvailable || isSomeSelectedKnmCreatedByErpDb;

  const isDropdownCancelItemsDisabled = !isSelectedKnmsTypePlan || isSomeSelectedKnmCreatedByErpDb; // ToDo: && not disapproved

  const exportNotify = ({ id }: IExport) => dispatch(addNoticeImportNotification(id));

  const handleExportKnmList = (format: ExportFormatsEnum) => () => {
    const currentFilter = activeKnmFilter ? mapKnmFilterToRaw(activeKnmFilter, isErknm) : undefined;

    dispatch(knmListExportList(exportNotify, format, columns, currentFilter));
  };

  const handleExportPlan = (format: ExportFormatsEnum, id?: number | string) => () => {
    if (id !== undefined) {
      dispatch(planListExportPlanById(format, Number(id)));
    }
  };

  const handleExportDocuments = useCallback(() => {
    if (currentPlanId !== undefined) {
      dispatch(planListExportDocsForKno(Number(currentPlanId)));
    }
  }, [currentPlanId, dispatch]);

  const handleExportSimilaritiesKnmList = (
    id: string,
    format: Exclude<ExportFormatsEnum, ExportFormatsEnum.PDF>,
    type: ExportSimilaritiesListTypesEnum,
  ) => () => {
    dispatch(knmListExportSimilaritiesList(exportNotify, id, format, type));
  };

  const handleExportRegistryKnm = (
    id: string,
    type: keyof Omit<
      IKnmCheckSubjectResult,
      'requestTime' | 'isOrganizationNameError' | 'isFnsAddressError' | 'isWorkAddressError'
    >,
  ) => () => dispatch(knmListExportRegistryKnm(exportNotify, Number(id), type));

  const restoreSelected = useCallback(() => {
    onRestoreClick();
  }, [onRestoreClick]);

  const renderDropdownTooltip = (text: string) => (
    <Tooltip
      overlayClassName={styles.DropdownTooltip}
      placement="top"
      content={<div className={styles.DropdownTooltipContent}>{text}</div>}
    >
      <span>
        <IconInfo className={styles.DropdownTooltipIcon} />
      </span>
    </Tooltip>
  );

  const handleRemoveFromPlanClick = useCallback(() => {
    if (isErknm) {
      const isAvailableKnms =
        isAllSelectedKnmsWithPlan &&
        filteredKnms.every(knm => knm.statusId === KnmErknmStatusIdsByCodeEnum.TYPE_WAITING_CARRY_OUT);

      dispatch(knmListRemoveFromPlanClick(isAvailableKnms, filteredKnms, openRemoveFromPlanModal));
    } else {
      openRemoveFromPlanModal();
    }
  }, [dispatch, filteredKnms, isAllSelectedKnmsWithPlan, isErknm, openRemoveFromPlanModal]);

  const disableClick = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
  }, []);

  const dropdownExportListItems: IDropdownItem[] = [
    {
      id: 'export-ods',
      content: (
        <DropdownItem onClick={handleExportKnmList(ExportFormatsEnum.ODS)}>Экспорт списка проверок в ODS</DropdownItem>
      ),
    },
    {
      id: 'export-xlsx',
      content: (
        <DropdownItem onClick={handleExportKnmList(ExportFormatsEnum.XLSX)}>
          Экспорт списка проверок в XLSX
        </DropdownItem>
      ),
    },
  ];

  const dropdownExportItems: IDropdownItem[] = [
    {
      id: 'export-ods-plan',
      content: (
        <DropdownItem
          onClick={handleExportPlan(ExportFormatsEnum.ODS, currentPlanId)}
          isDisabled={!isSingleKnmWithPlanSelected}
        >
          Экспорт плана в ODS
        </DropdownItem>
      ),
    },
    {
      id: 'export-xlsx-plan',
      content: (
        <DropdownItem
          onClick={handleExportPlan(ExportFormatsEnum.XLSX, currentPlanId)}
          isDisabled={!isSingleKnmWithPlanSelected}
        >
          Экспорт плана в XLSX
        </DropdownItem>
      ),
    },
  ];

  const dropdownExportItemsErp: IDropdownItem[] = [
    ...dropdownExportItems,
    {
      id: 'export-doc-complect',
      content: (
        <DropdownItem onClick={handleExportDocuments} isDisabled={!isSingleKnmWithPlanSelected}>
          Экспорт комплекта документов для КНО
        </DropdownItem>
      ),
    },
  ];

  const dropdownOutputItems: IDropdownItem[] = [
    {
      id: 'output-ods',
      content: (
        <DropdownItem
          onClick={handleExportSimilaritiesKnmList(
            selectedKnmsIds[0],
            ExportFormatsEnum.ODS,
            ExportSimilaritiesListTypesEnum.KNM_MATCH_BY_KNM,
          )}
          isDisabled={!isSingleKnmSelected || disabledExport}
        >
          Вывести совпадения по КНМ в ODS
        </DropdownItem>
      ),
    },
    {
      id: 'output-xlsx',
      content: (
        <DropdownItem
          onClick={handleExportSimilaritiesKnmList(
            selectedKnmsIds[0],
            ExportFormatsEnum.XLSX,
            ExportSimilaritiesListTypesEnum.KNM_MATCH_BY_KNM,
          )}
          isDisabled={!isSingleKnmSelected || disabledExport}
        >
          Вывести совпадения по КНМ в XLSX
        </DropdownItem>
      ),
    },
    {
      id: 'output-ods-plan-all',
      content: (
        <DropdownItem
          onClick={handleExportSimilaritiesKnmList(
            selectedKnmsIds[0],
            ExportFormatsEnum.ODS,
            ExportSimilaritiesListTypesEnum.KNM_MATCH_BY_PLAN_ALL,
          )}
          isDisabled={!isSingleKnmWithPlanSelected || disabledExport}
        >
          Вывести совпадения по плану (По всей РФ) в ODS
        </DropdownItem>
      ),
    },
    {
      id: 'output-xlsx-plan-all',
      content: (
        <DropdownItem
          onClick={handleExportSimilaritiesKnmList(
            selectedKnmsIds[0],
            ExportFormatsEnum.XLSX,
            ExportSimilaritiesListTypesEnum.KNM_MATCH_BY_PLAN_ALL,
          )}
          isDisabled={!isSingleKnmWithPlanSelected || disabledExport}
        >
          Вывести совпадения по плану (По всей РФ) в XLSX
        </DropdownItem>
      ),
    },
    {
      id: 'output-ods-plan',
      content: (
        <DropdownItem
          onClick={handleExportSimilaritiesKnmList(
            selectedKnmsIds[0],
            ExportFormatsEnum.ODS,
            ExportSimilaritiesListTypesEnum.KNM_MATCH_BY_PLAN,
          )}
          isDisabled={!isSingleKnmWithPlanSelected || disabledExport}
        >
          Вывести совпадения по плану (По субъекту РФ) в ODS
        </DropdownItem>
      ),
    },
    {
      id: 'output-xlsx-plan',
      content: (
        <DropdownItem
          onClick={handleExportSimilaritiesKnmList(
            selectedKnmsIds[0],
            ExportFormatsEnum.XLSX,
            ExportSimilaritiesListTypesEnum.KNM_MATCH_BY_PLAN,
          )}
          isDisabled={!isSingleKnmWithPlanSelected || disabledExport}
        >
          Вывести совпадения по плану (По субъекту РФ) в XLSX
        </DropdownItem>
      ),
    },
  ];

  const dropdownRegistryReportItems: IDropdownItem[] = [
    {
      id: 'spv-registry-report',
      content: (
        <DropdownItem
          onClick={handleExportRegistryKnm(selectedKnmsIds[0], 'spv')}
          isDisabled={!isSelectedIsSpvRegistry}
        >
          Отчет по реестру СПВ
        </DropdownItem>
      ),
    },
    {
      id: 'toser-registry-report',
      content: (
        <DropdownItem
          onClick={handleExportRegistryKnm(selectedKnmsIds[0], 'toser')}
          isDisabled={!isSelectedIsTorRegistry}
        >
          Отчет по реестру ТОСЭР
        </DropdownItem>
      ),
    },
  ];

  const dropdownDeleteItems: IDropdownItem[] = [
    {
      id: 'delete',
      content: (
        <DropdownItem onClick={handleDropdownDeleteButtonClick} isDanger isDisabled={isDeleteButtonDisabled}>
          Удалить
        </DropdownItem>
      ),
    },
  ];

  const dropdownRestoreItems: IDropdownItem[] = [
    {
      id: 'restore',
      content: (
        <DropdownItem onClick={restoreSelected} isSuccess isDisabled={isDropdownRestoreItemsDisabled}>
          Восстановить
        </DropdownItem>
      ),
    },
  ];

  const dropdownPlanOutItems: IDropdownItem[] = [
    {
      id: 'plan-out',
      content: (
        <DropdownItem onClick={handleRemoveFromPlanClick} isDisabled={isDropdownPlanOutItemsDisabled}>
          Исключить из плана
        </DropdownItem>
      ),
    },
  ];

  const dropdownTimeItems: IDropdownItem[] = [
    {
      id: 'knms-start-date-update',
      content: (
        <DropdownItem
          isDisabled={isDropdownStartDateUpdateItemDisabled}
          onClick={!isDropdownStartDateUpdateItemDisabled ? openUpdateKnmsStartDateModal : disableClick}
        >
          Изменить дату начала КНМ
          {renderDropdownTooltip('Операция доступна для не удаленных КНМ')}
        </DropdownItem>
      ),
    },
    {
      id: 'knms-duration-update',
      content: (
        <DropdownItem
          isDisabled={isDropdownDurationUpdateItemDisabled}
          onClick={!isDropdownDurationUpdateItemDisabled ? openUpdateKnmsDurationModal : disableClick}
        >
          Изменить срок проведения КНМ
          {renderDropdownTooltip('Операция доступна для плановых КНМ')}
        </DropdownItem>
      ),
    },
  ];

  const dropdownDisapproveItems: IDropdownItem[] = [
    {
      id: 'disapprove',
      content: (
        <DropdownItem onClick={!isDropdownDisapproveItemsDisabled ? openDisapproveKnmsModal : disableClick}>
          Отклонить
          {renderDropdownTooltip('Операция доступна для не аннулированных КНМ')}
        </DropdownItem>
      ),
    },
  ];

  const dropdownCancelItems: IDropdownItem[] = [
    {
      id: 'cancel',
      content: (
        <DropdownItem onClick={!isDropdownCancelItemsDisabled ? handleCancelKnmsButtonClick : disableClick}>
          Аннулировать
          {renderDropdownTooltip('Операция доступна для плановых КНМ')}
        </DropdownItem>
      ),
    },
  ];

  const getDropdownItems = () => {
    let res: IDropdownItem[] = [...dropdownExportListItems];

    if (!isOmbudsman) {
      res = [
        ...res,
        {
          id: 'divider-export',
          isDivider: true,
        },
        ...(isErknm ? dropdownExportItems : dropdownExportItemsErp),
        {
          id: 'divider-output',
          isDivider: true,
        },
        ...dropdownOutputItems,
        {
          id: 'divider-registry-report',
          isDivider: true,
        },
        ...dropdownRegistryReportItems,
        {
          id: 'divider-delete',
          isDivider: true,
        },
      ];
      if (isSupervisor || isProsecutorOrSuperProsecutor) {
        res = [...res, ...dropdownDeleteItems];
      }
      res = [...res, ...dropdownRestoreItems];
      if (!isErknm) {
        res = [...res, ...dropdownPlanOutItems, ...dropdownTimeItems];
      }
      if (isProsecutorOrSuperProsecutor && !isErknm) {
        res = [...res, ...dropdownDisapproveItems, ...dropdownCancelItems];
      }
    }

    return res;
  };

  if (isOmbudsman) {
    return null;
  }

  return <Dropdown disabled={dataCount < 1} items={getDropdownItems()} />;
};

const mapStateToProps = (state: IRootState) => ({
  isErknm: getLawInterface(state) === LawInterfacesEnum.FZ_248,
  isProsecutorOrSuperProsecutor: getUserRoleIsProsecutorOrSuperProsecutor(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  isOmbudsman: getUserRoleIsOmbudsman(state),
  searchInputValue: getKnmListSearchValue(state),
  activeKnmFilter: getKnmListFilterCurrentFilter(state),
});

export const KnmListDropdown = connect(mapStateToProps)(KnmListDropdownWithoutStore);
